import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CalendarDays, Check, Loader2, Phone, Save } from 'lucide-react';
import axios from '../../configApi/axiosConfig';
import { APIURL } from '../../configApi/apiConfig';
import { addCalendarDays, formatDate, toLocalDateInput, uniqueBy } from './gestorVentasUtils';

const FIELD_CLASS = 'mt-1.5 min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-morado focus:ring-2 focus:ring-morado/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500';

function ReadOnlyField({ label, value, icon: Icon }) {
  return (
    <div>
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <div className="mt-1.5 flex min-h-11 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700">
        {Icon && <Icon size={15} className="text-slate-400" aria-hidden="true" />}{value || '—'}
      </div>
    </div>
  );
}

export function GestionVentaForm({ oportunidad, catalogos, productos, onBack, onSaved, onDirtyChange }) {
  const rutas = useMemo(() => catalogos?.rutas || [], [catalogos?.rutas]);
  const [formas, setFormas] = useState([]);
  const [idAccion, setIdAccion] = useState('');
  const [idRespuesta, setIdRespuesta] = useState('');
  const [idDetalle, setIdDetalle] = useState('');
  const [idClasificacion, setIdClasificacion] = useState('');
  const [idProducto, setIdProducto] = useState('');
  const [comentario, setComentario] = useState('');
  const [fechaProxima, setFechaProxima] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const acciones = useMemo(() => uniqueBy(rutas, 'idAccion'), [rutas]);
  const respuestas = useMemo(() => uniqueBy(
    rutas.filter(r => String(r.idAccion) === idAccion && r.idRespuesta != null), 'idRespuesta'
  ), [rutas, idAccion]);
  const detalles = useMemo(() => uniqueBy(
    rutas.filter(r => String(r.idAccion) === idAccion
      && (r.idRespuesta == null || String(r.idRespuesta) === idRespuesta)
      && r.idDetalle != null), 'idDetalle'
  ), [rutas, idAccion, idRespuesta]);
  const clasificaciones = useMemo(() => rutas.filter(r =>
    String(r.idAccion) === idAccion
    && (r.idRespuesta == null ? !idRespuesta : String(r.idRespuesta) === idRespuesta)
    && (r.idDetalle == null ? !idDetalle : String(r.idDetalle) === idDetalle)
  ), [rutas, idAccion, idRespuesta, idDetalle]);
  const selectedRoute = useMemo(() => clasificaciones.find(r => String(r.idClasificacion) === idClasificacion), [clasificaciones, idClasificacion]);

  const isDirty = formas.length > 0 || idAccion || idRespuesta || idDetalle || idClasificacion || idProducto || comentario || fechaProxima;
  useEffect(() => { onDirtyChange?.(Boolean(isDirty)); }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!selectedRoute) {
      setIdProducto('');
      setFechaProxima('');
      return;
    }
    if (!selectedRoute.requiereProducto) setIdProducto('');
    if (selectedRoute.diasReprogramacion) setFechaProxima(addCalendarDays(selectedRoute.diasReprogramacion));
    else if (!selectedRoute.requiereProximaFecha) setFechaProxima('');
  }, [selectedRoute]);

  const toggleForma = id => setFormas(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);

  const submit = async event => {
    event.preventDefault();
    setError('');
    if (!formas.length) return setError('Selecciona al menos una forma de atención.');
    if (!selectedRoute) return setError('Completa el resultado de la gestión.');
    if (selectedRoute.requiereProducto && !idProducto) return setError('Selecciona el producto interesado.');
    if (selectedRoute.requiereComentario && !comentario.trim()) return setError('Ingresa el comentario requerido.');
    if (selectedRoute.requiereProximaFecha && !fechaProxima) return setError('Selecciona la próxima fecha de gestión.');

    setSaving(true);
    try {
      await axios.post(APIURL.postGestorVentasGestion(oportunidad.idOportunidad), {
        formasAtencionIds: formas,
        idRutaResultado: selectedRoute.idRutaResultado,
        ...(selectedRoute.requiereProducto ? { idProductoInteresado: Number(idProducto) } : {}),
        ...(comentario.trim() ? { comentario: comentario.trim() } : {}),
        ...(selectedRoute.requiereProximaFecha ? { fechaProximaGestion: fechaProxima } : {}),
      });
      onDirtyChange?.(false);
      onSaved();
    } catch (requestError) {
      const message = requestError?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(' ') : message || 'No fue posible guardar la gestión.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full space-y-5 pb-1" noValidate>
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-morado/30">
          <ArrowLeft size={17} /> Volver a la cola
        </button>
        <div className="flex flex-col items-end gap-1 text-right">
          <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-bold text-violet-800 ring-1 ring-violet-200">Nivel {oportunidad.prioridad} · {oportunidad.nivel}</span>
          {oportunidad.subnivel && <span className="text-xs font-semibold text-slate-600">Subnivel {oportunidad.prioridadSubnivel} · {oportunidad.subnivel}</span>}
        </div>
      </div>

      <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 md:mx-10 sm:mx-4 mx-2">
        <h3 className="text-base font-bold text-slate-900">{oportunidad.nombreCompleto}</h3>
        <p className="mt-1 text-sm text-slate-600">Cédula {oportunidad.cedula} · {oportunidad.productoOriginal || 'Sin producto original'}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ReadOnlyField label="Última gestión" value={formatDate(oportunidad.fechaUltimaGestion, true)} icon={CalendarDays} />
          <ReadOnlyField label="Fecha de gestión" value={formatDate(new Date(), true)} icon={CalendarDays} />
          <ReadOnlyField label="Teléfono" value={oportunidad.telefono || 'Sin teléfono'} icon={Phone} />
          <ReadOnlyField label="Cantidad de gestiones" value={String(oportunidad.cantidadGestiones || 0)} />
        </div>
      </section>

      <fieldset class="md:px-10 sm:px-4 px-2">
        <legend className="text-sm font-bold text-slate-800">Forma de atención <span className="text-red-600">*</span></legend>
        <p className="mt-1 text-xs text-slate-600">Puedes seleccionar más de una opción.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {(catalogos?.formasAtencion || []).map(forma => {
            const active = formas.includes(forma.idFormaAtencion);
            return (
              <button key={forma.idFormaAtencion} type="button" onClick={() => toggleForma(forma.idFormaAtencion)} aria-pressed={active}
                className={`inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-4 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-morado/30 ${active ? 'border-morado bg-violet-50 text-morado' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}>
                <span className={`flex h-5 w-5 items-center justify-center rounded border ${active ? 'border-morado bg-morado text-white' : 'border-slate-300 bg-white'}`}>{active && <Check size={13} />}</span>
                {forma.nombre}
              </button>
            );
          })}
        </div>
      </fieldset>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 md:px-10 sm:px-4 px-2">
        <label className="text-sm font-semibold text-slate-700">Acción <span className="text-red-600">*</span>
          <select className={FIELD_CLASS} value={idAccion} onChange={e => { setIdAccion(e.target.value); setIdRespuesta(''); setIdDetalle(''); setIdClasificacion(''); }}>
            <option value="">Selecciona</option>{acciones.map(item => <option key={item.idAccion} value={item.idAccion}>{item.accion}</option>)}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-700">Respuesta
          <select className={FIELD_CLASS} value={idRespuesta} disabled={!respuestas.length} onChange={e => { setIdRespuesta(e.target.value); setIdDetalle(''); setIdClasificacion(''); }}>
            <option value="">{respuestas.length ? 'Selecciona' : 'No aplica'}</option>{respuestas.map(item => <option key={item.idRespuesta} value={item.idRespuesta}>{item.respuesta}</option>)}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-700">Detalle
          <select className={FIELD_CLASS} value={idDetalle} disabled={!detalles.length} onChange={e => { setIdDetalle(e.target.value); setIdClasificacion(''); }}>
            <option value="">{detalles.length ? 'Selecciona' : 'No aplica'}</option>{detalles.map(item => <option key={item.idDetalle} value={item.idDetalle}>{item.detalle}</option>)}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-700">Clasificación <span className="text-red-600">*</span>
          <select className={FIELD_CLASS} value={idClasificacion} disabled={!clasificaciones.length} onChange={e => setIdClasificacion(e.target.value)}>
            <option value="">Selecciona</option>{clasificaciones.map(item => <option key={item.idRutaResultado} value={item.idClasificacion}>{item.clasificacion}</option>)}
          </select>
        </label>
      </section>

      {selectedRoute && (
        <section className="grid gap-4 rounded-xl border border-violet-200 bg-violet-50/40 p-4 sm:grid-cols-2 md:mx-10 sm:mx-4 mx-2">
          {selectedRoute.marcaResentido && (
            <div className="sm:col-span-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800" role="status">
              Esta estrategia marcará la oportunidad como Resentido y la moverá a Cerradas.
            </div>
          )}
          {selectedRoute.requiereProducto && (
            <label className="text-sm font-semibold text-slate-700">Producto interesado <span className="text-red-600">*</span>
              <select className={FIELD_CLASS} value={idProducto} onChange={e => setIdProducto(e.target.value)}>
                <option value="">Selecciona un producto</option>
                {productos.map(producto => <option key={producto.idCre_ProductoSolicitud} value={producto.idCre_ProductoSolicitud}>{producto.Producto}</option>)}
              </select>
            </label>
          )}
          {selectedRoute.requiereProximaFecha && (
            <label className="text-sm font-semibold text-slate-700">Próxima gestión <span className="text-red-600">*</span>
              <input type="date" min={toLocalDateInput()} className={FIELD_CLASS} value={fechaProxima} disabled={Boolean(selectedRoute.diasReprogramacion)} onChange={e => setFechaProxima(e.target.value)} />
              {selectedRoute.diasReprogramacion && <span className="mt-1 block text-xs text-slate-600">Fecha automática: +{selectedRoute.diasReprogramacion} días.</span>}
            </label>
          )}
          <label className={`text-sm font-semibold text-slate-700 ${selectedRoute.requiereProducto && selectedRoute.requiereProximaFecha ? 'sm:col-span-2' : ''}`}>Comentario de gestión {selectedRoute.requiereComentario && <span className="text-red-600">*</span>}
            <textarea className={`${FIELD_CLASS} min-h-28 resize-y`} maxLength={500} value={comentario} onChange={e => setComentario(e.target.value)} placeholder="Escribe un comentario breve y útil para el siguiente contacto." />
            <span className="mt-1 block text-right text-xs font-normal text-slate-500">{comentario.length}/500</span>
          </label>
        </section>
      )}

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700" role="alert">{error}</div>}

      <div className="flex justify-end pt-4 md:px-10 sm:px-4 px-2">
        <button type="submit" disabled={saving} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg bg-morado px-5 text-sm font-bold text-white transition-colors hover:bg-moradoHover focus:outline-none focus:ring-2 focus:ring-morado/40 disabled:cursor-not-allowed disabled:bg-slate-400">
          {saving ? <Loader2 size={17} className="animate-spin motion-reduce:animate-none" /> : <Save size={17} />}{saving ? 'Guardando…' : 'Guardar gestión'}
        </button>
      </div>
    </form>
  );
}
